import React from 'react';
import { allCountries } from 'country-telephone-data';
import { Control, FieldErrors } from 'react-hook-form';
import { DarwinCol, DarwinRow } from 'components/darwin-ui';
import DarwinTypography from 'components/darwin-ui/Typography';
import { UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styles from '../../Users.module.css';
import { CreateUserFormData, CreateUserFormInputProps } from 'types/userTypes';
import { InputType } from 'types/genericTypes';
import { USER_NAME_MAX_LENGTH, USER_PHONE_MAX_LENGTH } from 'utils/users';
import { GenericInput } from 'components/ui/GenericInput';

const { Title } = DarwinTypography;

interface PersonalInformationSectionProps {
  control: Control<CreateUserFormData>;
  errors: FieldErrors<CreateUserFormData>;
  canCreateEmailOrganizationRelation: boolean;
  selectedCountryCode: string;
  onSelectCountry?: (phoneCode: string) => void;
  isCreateForm: boolean;
}

export const PersonalInformationSection: React.FC<
  PersonalInformationSectionProps
> = ({
  control,
  errors,
  canCreateEmailOrganizationRelation,
  onSelectCountry,
  isCreateForm = false,
}) => {
  const { t } = useTranslation();

  const firstColumnInputs: CreateUserFormInputProps = [
    {
      inputType: InputType.INPUT,
      fieldName: 'firstName',
      label: t('create-user-form.name'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('create-user-form.first-name-required'),
        maxLength: {
          value: USER_NAME_MAX_LENGTH,
          message: t('create-user-form.max-length-error', {
            fieldName: t('create-user-form.name'),
            maxLength: USER_NAME_MAX_LENGTH,
          }),
        },
      },
      maxLength: USER_NAME_MAX_LENGTH,
      disabled: !canCreateEmailOrganizationRelation,
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'lastName',
      label: t('create-user-form.last-name'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('create-user-form.last-name-required'),
        maxLength: USER_NAME_MAX_LENGTH,
      },
      maxLength: USER_NAME_MAX_LENGTH,
      disabled: !canCreateEmailOrganizationRelation,
    },
  ];

  const secondColumnInputs: CreateUserFormInputProps = [
    {
      inputType: InputType.DROPDOWN,
      fieldName: 'countryCode',
      label: t('create-user-form.country-code'),
      showRequiredAsterisk: false,
      inputRules: {},
      dropdownData: allCountries.map(country => ({
        id: country.dialCode,
        label: country.name,
        phoneSelectKey: true,
      })),
      disabled: !canCreateEmailOrganizationRelation,
      onSelectCountry: onSelectCountry,
      enableSearchInDropdown: true,
    },
    {
      inputType: InputType.PHONE,
      fieldName: 'contactPhone',
      label: t('create-user-form.phone'),
      showRequiredAsterisk: true,
      inputRules: {},
      maxLength: USER_PHONE_MAX_LENGTH,
      disabled: !canCreateEmailOrganizationRelation,
      isCreateForm: isCreateForm,
    },
  ];

  return (
    <>
      <Title level={5} className={styles.userFormRowTitle}>
        <UserOutlined /> {t('create-user-form.personal-information')}
      </Title>
      <DarwinRow className={styles.userFormRow} gutter={16}>
        <DarwinCol span={12}>
          {firstColumnInputs.map(input => (
            <GenericInput
              key={input.fieldName}
              control={control}
              errors={errors}
              {...input}
            />
          ))}
        </DarwinCol>
        <DarwinCol span={12}>
          {secondColumnInputs.map(input => (
            <GenericInput
              key={input.fieldName}
              control={control}
              errors={errors}
              {...input}
            />
          ))}
        </DarwinCol>
      </DarwinRow>
    </>
  );
};
